import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LogoEmblem } from "./Logo";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  {
    label: "Solutions",
    children: [
      { to: "/school-os", label: "School OS" },
      { to: "/event-os", label: "Event & MUN OS" },
      { to: "/diplomexa", label: "Diplomexa Journeys" },
      { to: "/educational-tourism", label: "Educational Tourism" },
      { to: "/corporate-travel", label: "Institutional Travel" },
      { to: "/destination-management", label: "Destination Management" },
    ],
  },
  { to: "/partners", label: "Partner" },
  { to: "/insights", label: "Insights" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(false);
  }, [location.pathname]);

  const onLight = scrolled;
  const linkBase = onLight
    ? "text-navy/80 hover:text-navy"
    : "text-white/80 hover:text-white";
  const activeColor = onLight ? "text-navy" : "text-white";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        onLight
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <LogoEmblem size={48} variant="primary" />
          <span
            className={`font-display tracking-tight text-lg ${
              onLight ? "text-navy" : "text-white"
            }`}
          >
            Destineze
            <span
              className={`ml-2 text-[10px] uppercase tracking-[0.2em] hidden sm:inline-block ${
                onLight ? "text-slate-500" : "text-white/60"
              }`}
            >
              Educational Voyages
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(true)}
                onMouseLeave={() => setOpenDropdown(false)}
              >
                <button
                  data-testid="nav-solutions-trigger"
                  className={`flex items-center gap-1 text-sm font-medium transition ${linkBase}`}
                >
                  {item.label}
                  <ChevronDown size={14} />
                </button>
                {openDropdown && (
                  <div
                    data-testid="nav-solutions-menu"
                    className="absolute left-0 top-full pt-3 w-72"
                  >
                    <div className="bg-white border border-black/5 shadow-2xl rounded-md overflow-hidden">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-${c.to.replace("/", "")}`}
                          className={({ isActive }) =>
                            `block px-5 py-3 text-sm font-medium border-b border-black/5 last:border-0 transition ${
                              isActive
                                ? "bg-navy text-white"
                                : "text-navy hover:bg-navy/5"
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition ${
                    isActive ? activeColor : linkBase
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="navbar-cta-consultation"
            className={`px-5 py-2.5 text-sm font-semibold tracking-wide transition rounded-sm ${
              onLight
                ? "bg-navy text-white hover:bg-navy/90"
                : "bg-gold text-navy hover:bg-gold-300"
            }`}
          >
            Request Consultation
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className={`lg:hidden p-2 ${onLight ? "text-navy" : "text-white"}`}
          aria-label="menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-black/5"
        >
          <div className="max-w-[1280px] mx-auto px-5 py-4 flex flex-col">
            {NAV_LINKS.map((item) =>
              item.children ? (
                <div key={item.label} className="py-2">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                    {item.label}
                  </div>
                  {item.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      data-testid={`mobile-nav-${c.to.replace("/", "")}`}
                      className={({ isActive }) =>
                        `block py-2 text-base ${
                          isActive ? "text-navy font-semibold" : "text-navy/80"
                        }`
                      }
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 border-b border-black/5 text-base ${
                      isActive ? "text-navy font-semibold" : "text-navy/80"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
            <Link
              to="/contact"
              data-testid="mobile-cta-consultation"
              className="mt-4 px-5 py-3 text-center text-sm font-semibold bg-navy text-white rounded-sm"
            >
              Request Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
